import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { io, Socket } from "socket.io-client";
import axiosInstance from "../utils/axiosInterceptor";
import { getSocketOrigin } from "../utils/getSocketOrigin";
import {
  NOTIFICATIONS_BOOTSTRAP,
  NOTIFICATIONS_NEW,
  NOTIFICATIONS_SET_UNREAD_COUNT,
} from "../redux/types/common.types";

/**
 * REST bootstrap + Socket.IO for in-app notifications (after login).
 */
export function useNotificationsSocket() {
  const dispatch = useDispatch();
  const { signinuser } = useSelector((state: { common: any }) => state.common);
  const token = Cookies.get("token");
  const userId = signinuser?.userId;
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!token || !userId) return undefined;

    let cancelled = false;

    const bootstrap = async () => {
      try {
        const [countRes, listRes] = await Promise.all([
          axiosInstance.get("notifications/unread-count"),
          axiosInstance.get("notifications?limit=15&offset=0"),
        ]);
        if (cancelled) return;
        dispatch({
          type: NOTIFICATIONS_BOOTSTRAP,
          payload: {
            unreadCount: countRes.data?.count ?? 0,
            items: listRes.data?.Data ?? [],
          },
        });
      } catch {
        /* non-blocking */
      }
    };

    bootstrap();

    const origin = getSocketOrigin();
    if (!origin) return undefined;

    const socket = io(origin, {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("userLoggedIn", { userId: String(userId) });
    });

    socket.on("notification:new", (payload: unknown) => {
      dispatch({ type: NOTIFICATIONS_NEW, payload });
    });

    socket.on("notification:unreadCount", (payload: { count?: number }) => {
      if (typeof payload?.count === "number") {
        dispatch({
          type: NOTIFICATIONS_SET_UNREAD_COUNT,
          payload: { count: payload.count },
        });
      }
    });

    return () => {
      cancelled = true;
      socket.disconnect();
      socketRef.current = null;
    };
  }, [dispatch, token, userId]);
}
